// Components
import * as React from 'react';

// Misc
import { PubListConfigInternal } from '../types';
import { IntlShape, useIntl } from 'react-intl';

/** @jsx jsx */
import { jsx } from 'theme-ui';
import { useEffect } from 'react';
import {
  PubListConfig,
  PubListConfigListTypeEnum,
} from 'api/publist/models';
import PubListGeneratorMobile from './PubListGeneratorMobile';
import PubListGeneratorDesktop from './PubListGeneratorDesktop';
import { useBreakpointIndex } from '@theme-ui/match-media';

export interface Props {
  assetsAvailable: boolean;
  preview?: string;
  config: PubListConfigInternal;
  viewMessage?: string;
  loading?: boolean;
  emptyList?: boolean;
  onSuccess?: () => void;
  loadPreview: (config: PubListConfig) => any;
  storeConfig: (config: PubListConfig) => Promise<number> | null;
  storeNewConfig: (config: PubListConfig) => Promise<number>;
  updateConfig: (config: PubListConfigInternal) => any;
  alertShow: (message: string) => void;
  clearAllInput: (listType: PubListConfigListTypeEnum) => void;
}

/**
 * Backend is using a slightly different PublistConfig, e.g. person Ids are stored as a number
 * while frontend is using a ReferencedPersonDTO to display furhter information such as names.
 * Therefore some fields of a PublistConfig must be mapped before sending to the backend.
 *
 * @param config The internal (frontend) PublistConfig
 *
 * @returns The external (backend) PublistConfig
 */

export interface CategoryToggleConfig {
  determination_type: boolean;
  citation: boolean;
  group_content: boolean;
  person_filter_func: boolean;
  general_filter: boolean;
  publication_highlight: boolean;
  replace_text_links: boolean;
}

export const PubListGenerator: React.FC<Props> = ({
  config,
  alertShow,
  preview,
  updateConfig,
  loadPreview,
  storeConfig,
  storeNewConfig,
  assetsAvailable,
  loading,
  emptyList,
  onSuccess,
  viewMessage,
  clearAllInput
}) => {

  useEffect(() => {
    return () => {
      window.onbeforeunload = null;
    };
  });

  const intl: IntlShape = useIntl();
  const index = useBreakpointIndex({ defaultIndex: 0 });

  preview = preview?.replaceAll('>HB</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.hb' }) + '</a>')
    .replaceAll('>WoS</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.wos' }) + '</a>')
    .replaceAll('>PubMed</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.pubmed' }) + '</a>')
    .replaceAll('>Scopus</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.scopus' }) + '</a>')
    .replaceAll('>FullText</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.fulltext' }) + '</a>')
    .replaceAll('>Handle</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.handle' }) + '</a>')
    .replaceAll('>URN</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.urn' }) + '</a>');

    return index <= 2 ? (
      <PubListGeneratorMobile
        config={config}
        alertShow={alertShow}
        preview={preview}
        updateConfig={updateConfig}
        loadPreview={loadPreview}
        storeConfig={storeConfig}
        storeNewConfig={storeNewConfig}
        assetsAvailable={assetsAvailable}
        loading={loading}
        emptyList={emptyList}
        onSuccess={onSuccess}
        viewMessage={viewMessage}
        clearAllInput={clearAllInput}
      />
    ) : (
      <PubListGeneratorDesktop
        config={config}
        alertShow={alertShow}
        preview={preview}
        updateConfig={updateConfig}
        loadPreview={loadPreview}
        storeConfig={storeConfig}
        storeNewConfig={storeNewConfig}
        assetsAvailable={assetsAvailable}
        loading={loading}
        onSuccess={onSuccess}
        viewMessage={viewMessage}
        clearAllInput={clearAllInput}
      />
    );
};

export default PubListGenerator;
